import {collection, doc, addDoc, getDocs, updateDoc, deleteDoc, query, orderBy} from "firebase/firestore";
import {validateCourseLead} from "./courseValidation";

export const LEAD_STATUSES = {
    NEW: "new",
    CONTACTED: "contacted",
    ARCHIVED: "archived",
};

export const LEAD_STATUS_LABELS = {
    new: "Ny henvendelse",
    contacted: "Kontaktet",
    archived: "Arkivert",
};

export const LEAD_STATUS_COLORS = {
    new: "success",
    contacted: "info",
    archived: "default",
};

/**
 * Submits an interest inquiry from a prospective participant to an instructor.
 * Persists the lead in the instructor's isolated subcollection and queues a notification email.
 */
export const submitCourseLead = async (db, instructor, leadData) => {
    if (!db) throw new Error("Database-tilkobling mangler.");
    if (!instructor || !instructor.id) throw new Error("Mangler kursholder-informasjon.");

    const validation = validateCourseLead(leadData);
    if (!validation.isValid) {
        const firstErrorKey = Object.keys(validation.errors)[0];
        const error = new Error(validation.errors[firstErrorKey] || "Ugyldig skjema.");
        error.errors = validation.errors;
        throw error;
    }

    const now = new Date().toISOString();
    const newLead = {
        instructorId: instructor.id,
        instructorName: instructor.name || "",
        name: leadData.name.trim(),
        email: leadData.email.trim(),
        phone: (leadData.phone || "").trim(),
        courseId: leadData.courseId || null,
        courseTitle: leadData.courseTitle || "",
        message: (leadData.message || "").trim(),
        hasConsent: true,
        consentGiven: true,
        status: LEAD_STATUSES.NEW,
        createdAt: now,
        updatedAt: now,
    };

    // 1. Write to isolated subcollection
    const leadsRef = collection(db, "instructors", instructor.id, "leads");
    const docRef = await addDoc(leadsRef, newLead);

    // 2. Queue transactional notification to instructor if email exists
    if (instructor.email && typeof instructor.email === "string" && instructor.email.includes("@")) {
        try {
            const emailQueueRef = collection(db, "email");
            await addDoc(emailQueueRef, {
                to: instructor.email.trim(),
                message: {
                    subject: `Ny kurshenvendelse fra ${newLead.name} via Bekymringsmestring.no`,
                    text: `Hei ${instructor.name || "kursholder"}!\n\nDu har mottatt en ny henvendelse vedrørende kurs i bekymringsmestring:\n\nNavn: ${newLead.name}\nE-post: ${newLead.email}\nTelefon: ${newLead.phone || "Ikke oppgitt"}\nKommune / sted: ${instructor.place || ""}\nKurs: ${newLead.courseTitle || "Generell henvendelse"}\n\nMelding fra interessent:\n${newLead.message || "(Ingen melding oppgitt)"}\n\nDu kan følge opp henvendelsen ved å svare direkte på denne e-posten eller ved å logge inn på Min side:\nhttps://bekymringsmestring.no/me\n\nMed vennlig hilsen,\nBekymringsmestring.no`,
                },
                createdAt: now,
            });
        } catch (mailError) {
            console.warn("Could not queue notification email to /email queue:", mailError);
        }
    }

    return {
        id: docRef.id,
        ...newLead,
    };
};

/**
 * Fetches all leads for a specific instructor.
 */
export const fetchInstructorLeads = async (db, instructorId) => {
    if (!db || !instructorId) return [];
    const leadsRef = collection(db, "instructors", instructorId, "leads");
    const q = query(leadsRef, orderBy("createdAt", "desc"));
    const snapshot = await getDocs(q);
    const leads = [];
    snapshot.forEach((d) => {
        leads.push({ id: d.id, ...d.data() });
    });
    return leads;
};

/**
 * Updates status of a received lead.
 */
export const updateCourseLeadStatus = async (db, instructorId, leadId, newStatus) => {
    if (!db || !instructorId || !leadId) throw new Error("Ugyldige parametre.");
    const leadDocRef = doc(db, "instructors", instructorId, "leads", leadId);
    await updateDoc(leadDocRef, {
        status: newStatus,
        updatedAt: new Date().toISOString(),
    });
};

/**
 * Deletes a lead inquiry (GDPR right to erasure).
 */
export const deleteCourseLead = async (db, instructorId, leadId) => {
    if (!db || !instructorId || !leadId) throw new Error("Ugyldige parametre.");
    const leadDocRef = doc(db, "instructors", instructorId, "leads", leadId);
    await deleteDoc(leadDocRef);
};
